import { isFiniteNumber } from "@bitwarden/web-vault/app/shared/utils/helper-number-format";
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  OnInit,
  signal,
  viewChild,
  WritableSignal,
} from "@angular/core";
import { GlossScrollComponent } from "@bitwarden/web-vault/app/components/gloss-scroll/gloss-scroll.component";
import {
  CreditsService,
  CreditPurchaseRecord,
} from "@bitwarden/web-vault/app/services/syncing/gloss-credits/credits.service";
import { TransactionType } from "@bitwarden/web-vault/app/models/enum/credits.enum";
import { WATCH_AD_REWARD } from "@bitwarden/web-vault/app/models/constants/credits.constants";
import {
  GLOSS_CREDITS_SYMBOL,
  GLOSS_PLATINUM_CREDITS_SYMBOL,
} from "@bitwarden/web-vault/app/models/constants/global.constants";
import { ITransaction } from "@bitwarden/web-vault/app/models/interfaces/credits.interfaces";
import { GlossDateFactory } from "@bitwarden/web-vault/app/models/data/shared/gloss-date/gloss-date.factory";
import { prefersReducedMotion } from "@bitwarden/web-vault/app/shared/utils/reduced-motion.util";

// The ad-reward amount historical (no-`source`-field) deposit transactions were actually
// recorded with, back when watching an ad granted 10 Gold credits (CrS). Checked alongside
// (not instead of) the live `WATCH_AD_REWARD` from credits.constants.ts, for source-less rows:
// non-PROD ad watches (1 CrP), which GLOSS writes itself, had no source before 2026-07-14, then
// were written "manual" until the backend took "ad_reward" from the client.
// bank-management-system's backfill-transaction-source labels every legacy row it can tell
// (purchases, the registration bonus, conversions, those ad watches, and the ones written
// "manual"); only the rows it can't tell stay source-less and reach this guess.
const LEGACY_AD_REWARD_AMOUNT = 10;

// Whether a deposit is an ad-watch reward, for the icon/title/accent only (the unit is read off
// transaction.currency). A `source` of "ad_reward" is authoritative. A source-less row can only
// be matched by amount, so the amount is paired with the currency that ad rewards were actually
// paid in: 10 CrS before the 2026-06-30 switch (and still on PROD, where the backend SSV callback
// has yet to move to CrP — see ad-list.component.ts), 1 CrP since. That keeps a source-less 1 CrS
// Gold deposit from reading as an ad watch. A source-less 10 CrS Gold deposit remains
// indistinguishable from a legacy ad reward; only a backend backfill of `source` can fix that.
function isAdRewardDeposit(transaction: ITransaction): boolean {
  if (transaction.source === "ad_reward") {
    return true;
  }
  if (transaction.source) {
    return false;
  }
  return (
    (transaction.amount === LEGACY_AD_REWARD_AMOUNT &&
      transaction.currency === GLOSS_CREDITS_SYMBOL) ||
    (transaction.amount === WATCH_AD_REWARD &&
      transaction.currency === GLOSS_PLATINUM_CREDITS_SYMBOL)
  );
}

type ActivityTag = "earned" | "purchased" | "redeemed" | "converted";
type ActivityFilter = "all" | ActivityTag;
// "converted" items reuse the earned/redeemed accent (below) rather than getting their own —
// same pattern as ad-reward items reusing "ads" while their tag stays "earned".
type AccentKey = "earned" | "purchased" | "redeemed" | "ads";

interface ActivityItem {
  id: string;
  title: string;
  timestamp: number;
  formattedDate: string;
  // null when the backend didn't say how much (a completed purchase without credits): the row
  // shows no amount rather than "+0", as the purchase receipt does (#5352).
  amount: number | null;
  tag: ActivityTag;
  icon: string;
  chipClass: string;
  iconColor: string;
  categoryLabel: string;
  unit: "CrG" | "CrP";
}

// Row entrance — list-motion prototype, Rewards › Recent Activity, variant A "Cascade · S04"
// (motion scenario S04 · Group entrance stagger, the Today › Accounts entrance): each row rises
// 8px and fades in over var(--duration-slower) with var(--ease-out), +50ms per row, capped so
// rows 8+ enter together with the 8th. Old rows are cut (no exit); the list body eases to its new
// height on the same clock. Reduced motion: opacity only over var(--duration-fast), no travel,
// no stagger, no height easing.
const ENTRANCE_DURATION_MS = 300; // --duration-slower
const ENTRANCE_STAGGER_MS = 50;
const ENTRANCE_STAGGER_CAP = 8;
const ENTRANCE_TRAVEL_PX = 8;
const REDUCED_FADE_MS = 150; // --duration-fast
// Negative insets keep the rows' side/bottom shadows visible while the bottom edge is clipped
// during the height ease.
const HEIGHT_EASE_CLIP = "inset(-16px -16px -12px -16px)";
const EASE_OUT = "cubic-bezier(0.23, 1, 0.32, 1)"; // --ease-out
// Longest the first entrance waits for purchase history (capFetch has no timeout), so a slow
// request can't keep already-loaded ledger rows hidden.
export const PURCHASE_HISTORY_WAIT_MS = 1000;

// The credits ledger's day, as before.
const ACTIVITY_TIME_ZONE = "Australia/Sydney";

/**
 * A row's sort time and shown date, the date through GlossDate's own ISO → local-date conversion
 * rather than a copy of it (#3756; only the date is shown, so no time is kept).
 * A createdAt that's missing or isn't a date sorts last and shows no date: it used to throw out of
 * the whole list (#3753), or show as 1970-01-01, and a NaN time would break the sort.
 */
function activityDate(createdAt: string | null | undefined): {
  timestamp: number;
  formattedDate: string;
} {
  const timestamp = createdAt ? new Date(createdAt).getTime() : NaN;
  if (Number.isNaN(timestamp)) {
    return { timestamp: 0, formattedDate: "" };
  }
  const formattedDate = GlossDateFactory.createFromISOString(
    new Date(timestamp).toISOString(),
    false,
    ACTIVITY_TIME_ZONE,
  ).dateString;
  return { timestamp, formattedDate };
}

// Decorative accent per activity type (design-system-2.1-tokens.json `activity-*` — named
// without the `color-` prefix so generate-dt-tokens.js's group-from-first-two-dash-segments
// splits `activity-<type>-<bg|text>` into group `activity-<type>` / key `bg`|`text`; a
// `color-activity-*` name would group as `color-activity` instead and silently break these
// classes). Glyph uses the "*-mono" sprite variants (currentColor) via <icon [currentColor]>,
// chip is the bg class.
const ACCENT: Record<AccentKey, { chipClass: string; iconColor: string }> = {
  earned: { chipClass: "tw-bg-activity-earned-bg", iconColor: "activity-earned-text" }, // cyan
  purchased: { chipClass: "tw-bg-activity-purchased-bg", iconColor: "activity-purchased-text" }, // indigo
  redeemed: { chipClass: "tw-bg-activity-redeemed-bg", iconColor: "activity-redeemed-text" }, // pink
  ads: { chipClass: "tw-bg-activity-ads-bg", iconColor: "activity-ads-text" }, // violet
};

@Component({
  selector: "app-recent-activity",
  templateUrl: "./recent-activity.component.html",
})
export class RecentActivityComponent implements OnInit {
  private creditsService = inject(CreditsService);
  private injector = inject(Injector);

  private activityBody = viewChild<ElementRef<HTMLElement>>("activityBody");
  private glossScroll = viewChild(GlossScrollComponent);

  // True until the first entrance has played. The rows stay hidden meanwhile so they don't paint
  // once and then snap back to opacity 0 when the entrance starts.
  readonly entrancePending = signal(true);
  private isInView = signal(false);
  private visibilityObserver: IntersectionObserver | null = null;
  private runningAnimations: Animation[] = [];

  readonly filterOptions: ActivityFilter[] = [
    "all",
    "earned",
    "purchased",
    "redeemed",
    "converted",
  ];

  selectedFilter: WritableSignal<ActivityFilter> = signal("all");
  // null until loaded (or until the first entrance stops waiting for it).
  private purchaseHistory = signal<CreditPurchaseRecord[] | null>(null);

  constructor() {
    afterNextRender(() => this.observeVisibility());

    // First entrance waits for the full list (purchase history merged in, so purchases don't pop
    // in after the cascade) and for the list to actually be on screen: on mobile the card starts
    // collapsed and this component's DOM stays detached until it's expanded, so playing at init
    // would run unseen.
    effect(() => {
      if (
        this.entrancePending() &&
        this.purchaseHistory() !== null &&
        this.isInView() &&
        this.filteredItems().length
      ) {
        afterNextRender(
          () => {
            if (this.entrancePending()) {
              this.playEntrance(null);
            }
          },
          { injector: this.injector },
        );
      }
    });

    inject(DestroyRef).onDestroy(() => {
      this.visibilityObserver?.disconnect();
      this.cancelAnimations();
    });
  }

  async ngOnInit(): Promise<void> {
    // A slow request mustn't keep the rows hidden; purchases arriving later just pop in.
    const fallback = setTimeout(
      () => this.purchaseHistory.update((history) => history ?? []),
      PURCHASE_HISTORY_WAIT_MS,
    );
    try {
      this.purchaseHistory.set(await this.creditsService.getPurchaseHistory());
    } catch {
      this.purchaseHistory.set([]);
    } finally {
      clearTimeout(fallback);
    }
  }

  // A completed purchase deposits into the same ledger as earned rewards, distinguished only
  // by the backend's Transaction.source field — exclude those here so purchasedItems (sourced
  // from getPurchaseHistory()) is the single place a purchase shows up. Transactions written
  // before this field existed have no source; ad-watch deposits among those are still inferred
  // by amount and currency as a fallback (see isAdRewardDeposit). Known gap: a legacy purchase deposit (no
  // source) has no equivalent fallback and will still double-count against purchasedItems —
  // there's no reliable id-free way to match it without risking misapplied categories on real
  // financial data. Needs a backend backfill of `source` on historical transactions; flagged as
  // a follow-up, not silently solved here.
  private earnedItems = computed<ActivityItem[]>(() =>
    this.creditsService
      .creditTransactions()
      .filter((transaction) => transaction.type === TransactionType.DEPOSIT)
      .filter((transaction) => transaction.source !== "purchase")
      .map((transaction) => {
        const isAdReward = isAdRewardDeposit(transaction);
        const isConversion = transaction.source === "conversion";
        return {
          id: transaction.id,
          ...activityDate(transaction.createdAt),
          title:
            transaction.description ||
            (isConversion ? "creditsConvertedIn" : isAdReward ? "watchAds" : "creditsEarned"),
          amount: transaction.amount,
          tag: (isConversion ? "converted" : "earned") as ActivityTag,
          icon: isAdReward ? "watch-ads-mono" : "earned-mono",
          categoryLabel: isAdReward ? "earnedAds" : isConversion ? "converted" : "earned",
          // Read off the row itself, same as redeemedItems — isAdReward is a heuristic for the
          // icon/title/accent, and inferring the unit from it mislabels e.g. a source-less Gold
          // deposit that happens to equal an ad-reward amount (1 or 10) as CrP.
          unit:
            transaction.currency === GLOSS_PLATINUM_CREDITS_SYMBOL
              ? ("CrP" as const)
              : ("CrG" as const),
          ...ACCENT[isAdReward ? "ads" : "earned"],
        };
      }),
  );

  private redeemedItems = computed<ActivityItem[]>(() =>
    this.creditsService
      .creditTransactions()
      .filter((transaction) => transaction.type === TransactionType.WITHDRAWAL)
      .map((transaction) => {
        const isConversion = transaction.source === "conversion";
        return {
          id: transaction.id,
          ...activityDate(transaction.createdAt),
          title:
            transaction.description ||
            (isConversion ? "creditsConvertedOut" : "monthlySubscription"),
          amount: -transaction.amount,
          tag: (isConversion ? "converted" : "redeemed") as ActivityTag,
          icon: "gift-mono",
          categoryLabel: isConversion ? "converted" : "redeemed",
          unit:
            transaction.currency === GLOSS_PLATINUM_CREDITS_SYMBOL
              ? ("CrP" as const)
              : ("CrG" as const),
          ...ACCENT.redeemed,
        };
      }),
  );

  // Purchases are sourced separately from getPurchaseHistory() rather than the ledger's
  // DEPOSIT rows, since it carries purchase-specific fields (productId, newBalance) the
  // ledger doesn't have; earnedItems excludes the matching ledger rows via source === "purchase".
  private purchasedItems = computed<ActivityItem[]>(() =>
    (this.purchaseHistory() ?? [])
      .filter((purchase) => purchase.status === "completed")
      .map((purchase) => {
        return {
          id: purchase.purchaseId,
          ...activityDate(purchase.createdAt),
          title: "creditPurchase",
          amount: isFiniteNumber(purchase.credits) ? purchase.credits : null,
          tag: "purchased" as const,
          icon: "purchased-mono",
          categoryLabel: "purchased",
          unit: "CrG" as const,
          ...ACCENT.purchased,
        };
      }),
  );

  readonly allItems = computed<ActivityItem[]>(() =>
    [...this.earnedItems(), ...this.purchasedItems(), ...this.redeemedItems()].sort(
      (a, b) => b.timestamp - a.timestamp,
    ),
  );

  readonly filteredItems = computed<ActivityItem[]>(() => {
    const filter = this.selectedFilter();
    return this.allItems().filter((item) => filter === "all" || item.tag === filter);
  });

  setFilter(filter: ActivityFilter): void {
    if (filter === this.selectedFilter()) {
      return;
    }
    // Read the body's on-screen height before re-rendering (mid-ease if a previous change is
    // still playing), so an interrupted change retargets from where it is instead of jumping.
    const fromHeight = this.activityBody()?.nativeElement.offsetHeight ?? null;
    this.glossScroll()?.scrollToTop();
    this.selectedFilter.set(filter);
    afterNextRender(() => this.playEntrance(fromHeight), { injector: this.injector });
  }

  private observeVisibility(): void {
    const body = this.activityBody()?.nativeElement;
    if (!body || typeof IntersectionObserver === "undefined") {
      this.isInView.set(true);
      return;
    }
    this.visibilityObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.isInView.set(true);
        this.visibilityObserver?.disconnect();
        this.visibilityObserver = null;
      }
    });
    this.visibilityObserver.observe(body);
  }

  // Every rendered row re-enters top-down — rows kept across a filter change replay too, so the
  // whole list reads as one cascade. `fromHeight` is null for the first entrance (no height ease).
  private playEntrance(fromHeight: number | null): void {
    this.entrancePending.set(false);
    this.cancelAnimations();

    const body = this.activityBody()?.nativeElement;
    if (!body || typeof body.animate !== "function") {
      return;
    }
    body.style.clipPath = "";
    const easing = EASE_OUT;

    if (prefersReducedMotion()) {
      this.track(
        body.animate([{ opacity: 0 }, { opacity: 1 }], { duration: REDUCED_FADE_MS, easing }),
      );
      return;
    }

    const toHeight = body.offsetHeight;
    if (fromHeight !== null && Math.abs(fromHeight - toHeight) >= 1) {
      body.style.clipPath = HEIGHT_EASE_CLIP;
      const heightEase = body.animate(
        [{ height: `${fromHeight}px` }, { height: `${toHeight}px` }],
        { duration: ENTRANCE_DURATION_MS, easing },
      );
      // onfinish doesn't fire on cancel — a newer change resets the clip itself.
      heightEase.onfinish = () => (body.style.clipPath = "");
      this.track(heightEase);
    }

    body.querySelectorAll<HTMLElement>("[data-activity-row]").forEach((row, index) => {
      this.track(
        row.animate(
          [
            { opacity: 0, transform: `translateY(${ENTRANCE_TRAVEL_PX}px)` },
            { opacity: 1, transform: "none" },
          ],
          {
            duration: ENTRANCE_DURATION_MS,
            delay: Math.min(index, ENTRANCE_STAGGER_CAP - 1) * ENTRANCE_STAGGER_MS,
            easing,
            fill: "backwards",
          },
        ),
      );
    });
  }

  private track(animation: Animation): void {
    this.runningAnimations.push(animation);
  }

  private cancelAnimations(): void {
    this.runningAnimations.forEach((animation) => animation.cancel());
    this.runningAnimations = [];
  }
}
